// Lecture position editing, exercise recording, and lecture timer actions.
import type { Dispatch, SetStateAction } from 'react';
import { getSocket } from '../socket';
import type { Color, Role, RoomState } from '../types';
import { boardFromFen, fenFromBoard, startBoard, type BoardMap, type PieceCode } from '../board/pieces';

export type PendingPromotion = {
  from: string;
  to: string;
  color: Color;
  context: 'game' | 'recording' | 'exercise';
};

type LectureActionContext = {
  roomId: string;
  role: Role;
  state: RoomState | null;
  board: BoardMap;
  recording: boolean;
  recorded: string[];
  recordingStartFen: string;
  lectureSeconds: number;
  lectureRemaining: number;
  selected: string | null;
  spare: PieceCode | null;
  setState: Dispatch<SetStateAction<RoomState | null>>;
  setBoard: Dispatch<SetStateAction<BoardMap>>;
  setRecorded: Dispatch<SetStateAction<string[]>>;
  setRecording: Dispatch<SetStateAction<boolean>>;
  setRecordingStartFen: Dispatch<SetStateAction<string>>;
  setLectureSeconds: Dispatch<SetStateAction<number>>;
  setPendingPromotion: Dispatch<SetStateAction<PendingPromotion | null>>;
  setSelected: Dispatch<SetStateAction<string | null>>;
  setSpare: Dispatch<SetStateAction<PieceCode | null>>;
  setStatus: Dispatch<SetStateAction<string>>;
};

const socket = getSocket();

export function createLectureActions(context: LectureActionContext) {
  const {
    roomId, role, state, board, recording, recorded, recordingStartFen, lectureSeconds,
    lectureRemaining, selected, spare, setState, setBoard, setRecorded, setRecording,
    setRecordingStartFen, setLectureSeconds, setPendingPromotion, setSelected, setSpare,
    setStatus
  } = context;

  function localBoardState(nextBoard: BoardMap, nextTurn: Color) {
    const fen = fenFromBoard(nextBoard, nextTurn);
    setBoard(nextBoard);
    setState((previous) => previous ? { ...previous, fen, turn: nextTurn } : previous);
  }

  function publishBoard(nextBoard: BoardMap, nextTurn: Color) {
    if (role !== 'trainer' || state?.mode !== 'lecture') return;
    const fen = fenFromBoard(nextBoard, nextTurn);
    localBoardState(nextBoard, nextTurn);
    if (!recording) socket.emit('setBoard', { roomId, fen, turn: nextTurn });
  }

  function isPromotionTarget(piece: PieceCode | undefined, to: string) {
    return Boolean(
      piece?.[1] === 'P' &&
      ((piece[0] === 'w' && to[1] === '8') || (piece[0] === 'b' && to[1] === '1'))
    );
  }

  function moveBoard(from: string, to: string) {
    if (role !== 'trainer' || state?.mode !== 'lecture') return;
    const piece = board[from];
    if (!piece || from === to) return;
    const targetPiece = board[to];
    if (targetPiece && targetPiece[0] === piece[0]) return;

    if (recording && isPromotionTarget(piece, to)) {
      setPendingPromotion({ from, to, color: piece[0] as Color, context: 'recording' });
      setSelected(null);
      return;
    }

    const next = { ...board };
    delete next[from];
    next[to] = piece;

    let moveText = from + '-' + to;
    if (piece === 'wK' && from === 'e1' && to === 'g1' && next.h1 === 'wR') {
      moveText = 'O-O';
      delete next.h1;
      next.f1 = 'wR';
    } else if (piece === 'wK' && from === 'e1' && to === 'c1' && next.a1 === 'wR') {
      moveText = 'O-O-O';
      delete next.a1;
      next.d1 = 'wR';
    } else if (piece === 'bK' && from === 'e8' && to === 'g8' && next.h8 === 'bR') {
      moveText = 'O-O';
      delete next.h8;
      next.f8 = 'bR';
    } else if (piece === 'bK' && from === 'e8' && to === 'c8' && next.a8 === 'bR') {
      moveText = 'O-O-O';
      delete next.a8;
      next.d8 = 'bR';
    }

    if (recording) {
      const moveColor: Color = piece[0] === 'b' ? 'b' : 'w';
      setRecorded((previous) => [...previous, moveColor + ':' + moveText]);
      localBoardState(next, moveColor === 'w' ? 'b' : 'w');
      return;
    }

    publishBoard(next, state?.turn || 'w');
  }

  function trainerLectureClick(square: string) {
    if (role !== 'trainer' || state?.mode !== 'lecture') {
      setSelected(null);
      setSpare(null);
      return;
    }

    if (spare) {
      const next = { ...board, [square]: spare };
      setSpare(null);
      setSelected(null);
      publishBoard(next, state?.turn || 'w');
      return;
    }

    if (!selected) {
      if (board[square]) setSelected(square);
      return;
    }

    moveBoard(selected, square);
    setSelected(null);
  }

  function startRecording() {
    if (role !== 'trainer' || state?.mode !== 'lecture') return;
    if (state.lectureTimer.active) {
      setStatus('Остановите таймер перед записью задачи');
      return;
    }

    const currentFen = state?.fen || fenFromBoard(board, state?.turn || 'w');
    setRecordingStartFen(currentFen);
    setRecorded([]);
    setRecording(true);
    setStatus('Запись решения началась');
  }

  function finishRecording() {
    if (role !== 'trainer' || state?.mode !== 'lecture') return;
    if (state.lectureTimer.active) {
      setStatus('Остановите таймер перед публикацией задачи');
      return;
    }

    setRecording(false);
    const restoredBoard = boardFromFen(recordingStartFen);
    const restoredTurn: Color = recordingStartFen.split(/\s+/)[1] === 'b' ? 'b' : 'w';
    localBoardState(restoredBoard, restoredTurn);

    socket.emit('publishExercise', {
      roomId,
      sequence: recorded.join(', '),
      initialFen: recordingStartFen
    });

    setStatus('Публикация задачи…');
  }

  function setLectureTurn(color: Color) {
    if (role !== 'trainer' || state?.mode !== 'lecture' || recording) return;
    publishBoard(board, color);
  }

  function clearBoard() {
    if (role !== 'trainer' || state?.mode !== 'lecture' || recording) return;

    const next: BoardMap = {};
    const nextTurn: Color = 'w';
    const initial = fenFromBoard(next, nextTurn);
    localBoardState(next, nextTurn);
    socket.emit('publishExercise', { roomId, sequence: '', initialFen: initial });
    setRecorded([]);
    setSelected(null);
    setSpare(null);
  }

  function removePiece(square: string) {
    if (role !== 'trainer' || state?.mode !== 'lecture' || recording) return;
    const next = { ...board };
    delete next[square];
    publishBoard(next, state.turn);
  }

  function resetPosition() {
    if (role !== 'trainer' || state?.mode !== 'lecture' || recording) return;

    const initialBoard = startBoard();
    const initial = fenFromBoard(initialBoard, 'w');
    localBoardState(initialBoard, 'w');
    socket.emit('publishExercise', { roomId, sequence: '', initialFen: initial });
    setRecorded([]);
    setSelected(null);
    setSpare(null);
  }

  function startLectureTimer() {
    if (role !== 'trainer' || state?.mode !== 'lecture' || recording) return;
    if (recording) {
      setStatus('Завершите запись перед запуском таймера');
      return;
    }

    const seconds = Math.min(3600, Math.max(1, Math.floor(lectureSeconds) || 1));
    setLectureSeconds(seconds);
    socket.emit('startLectureTimer', { roomId, seconds });
  }

  function adjustLectureTimer(delta: number) {
    if (role !== 'trainer' || state?.mode !== 'lecture') return;

    if (!state.lectureTimer.active) {
      setLectureSeconds((value) => Math.min(3600, Math.max(1, value + delta)));
      return;
    }

    const remainingSeconds = Math.ceil(lectureRemaining / 1000) + delta;
    if (remainingSeconds <= 0) {
      socket.emit('stopLectureTimer', { roomId });
      return;
    }
    socket.emit('startLectureTimer', { roomId, seconds: Math.min(3600, remainingSeconds) });
  }

  function stopLectureTimer() {
    if (role !== 'trainer' || state?.mode !== 'lecture') return;
    socket.emit('stopLectureTimer', { roomId });
  }

  return {
    localBoardState,
    publishBoard,
    isPromotionTarget,
    moveBoard,
    trainerLectureClick,
    startRecording,
    finishRecording,
    setLectureTurn,
    clearBoard,
    removePiece,
    resetPosition,
    startLectureTimer,
    adjustLectureTimer,
    stopLectureTimer
  };
}
